import {
  ArrowRightOutlined,
  CameraOutlined,
  CheckCircleFilled,
  CloseOutlined,
  FilePdfOutlined,
  PictureOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import {
  DOCUMENT_TYPE_LABELS,
  DOCUMENT_TYPES,
  type DocumentType,
  describeError,
  isCorrespondence,
  isPdf,
  photoErrorMessage,
  present,
  today,
  uploadFile,
} from '@horof/shared';
import { Alert, App, Button, Input, Progress, Radio, Select } from 'antd';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { useClient, useQuery } from 'urql';
import {
  documentsContext,
  PhoneAddFileDocument,
  PhoneCounterpartiesDocument,
  PhoneCreateDocumentDocument,
} from './documents-data';

type Page = {
  key: number;
  file: File;
  preview: string | null;
  pdf: boolean;
  progress: number;
  photoId: string | null;
  error: string | null;
};

/** The usual ones first, as big buttons; the others in a list. */
const QUICK: DocumentType[] = ['CORRESPONDENCE_IN', 'CORRESPONDENCE_OUT'];
const QUICK_LABELS: Partial<Record<DocumentType, string>> = {
  CORRESPONDENCE_IN: 'رسالة واردة إلينا',
  CORRESPONDENCE_OUT: 'رسالة صادرة منا',
};

/**
 * «تصوير وثيقة»: a paper document page by page with the camera (each page is
 * sent as soon as it is taken), then what it is. Saved as one document with
 * its pages in order (arqam doc 52). Letters get their number from arqam.
 */
export function ScanPage() {
  const navigate = useNavigate();
  const client = useClient();
  const { message, modal } = App.useApp();
  const camera = useRef<HTMLInputElement>(null);
  const gallery = useRef<HTMLInputElement>(null);
  const counter = useRef(0);
  const [pages, setPages] = useState<Page[]>([]);
  const [step, setStep] = useState<'pages' | 'details'>('pages');
  const [type, setType] = useState<DocumentType | null>(null);
  const [title, setTitle] = useState('');
  const [date, setDate] = useState(today());
  const [counterpartyId, setCounterpartyId] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [{ data: parties }] = useQuery({ query: PhoneCounterpartiesDocument });

  // Previews are local files: freed when leaving.
  const pagesRef = useRef(pages);
  pagesRef.current = pages;
  useEffect(
    () => () => {
      for (const p of pagesRef.current) {
        if (p.preview) URL.revokeObjectURL(p.preview);
      }
    },
    [],
  );

  const patch = (key: number, change: Partial<Page>) =>
    setPages((all) =>
      all.map((p) => (p.key === key ? { ...p, ...change } : p)),
    );

  async function send(page: Page) {
    patch(page.key, { error: null, progress: 0 });
    try {
      const photo = await uploadFile(page.file, 'DOCUMENT', (progress) =>
        patch(page.key, { progress }),
      );
      patch(page.key, { photoId: photo.id, progress: 1 });
    } catch (error) {
      patch(page.key, { error: photoErrorMessage(error) });
    }
  }

  function add(files: File[]) {
    for (const file of files) {
      counter.current += 1;
      const pdf = isPdf(file);
      const page: Page = {
        key: counter.current,
        file,
        pdf,
        preview: pdf ? null : URL.createObjectURL(file),
        progress: 0,
        photoId: null,
        error: null,
      };
      setPages((all) => [...all, page]);
      send(page);
    }
  }

  function remove(page: Page) {
    if (page.preview) URL.revokeObjectURL(page.preview);
    setPages((all) => all.filter((p) => p.key !== page.key));
  }

  const sending = pages.some((p) => !p.photoId && !p.error);
  const failed = pages.some((p) => p.error);
  const ready = pages.length > 0 && !sending && !failed;

  async function leave() {
    if (pages.length) {
      const ok = await modal.confirm({
        title: 'إلغاء التصوير؟',
        content: 'لم تُحفظ الوثيقة بعد: تُلغى الصفحات المصوَّرة.',
        okText: 'نعم، ألغِ',
        okButtonProps: { danger: true },
        cancelText: 'رجوع',
      });
      if (!ok) return;
    }
    navigate('/documents');
  }

  async function save() {
    setProblem(null);
    if (!type) return setProblem('اختر نوع الوثيقة.');
    if (!title.trim()) return setProblem('اكتب عنواناً قصيراً للوثيقة.');
    if (!date) return setProblem('اختر التاريخ.');
    setSaving(true);
    const created = await client
      .mutation(
        PhoneCreateDocumentDocument,
        {
          doc: {
            type,
            title: title.trim(),
            documentDate: date,
            counterpartyId,
          },
        },
        documentsContext,
      )
      .toPromise();
    const doc = created.data?.createAssociationDocument?.associationDocument;
    if (created.error || !doc) {
      setSaving(false);
      setProblem(describeError(created.error, 'تعذّر حفظ الوثيقة.'));
      return;
    }
    for (const [i, p] of pages.entries()) {
      const r = await client
        .mutation(
          PhoneAddFileDocument,
          {
            file: {
              documentId: doc.id,
              photoId: p.photoId as string,
              position: i + 1,
            },
          },
          documentsContext,
        )
        .toPromise();
      if (r.error) {
        setSaving(false);
        // The document exists: its page says what is there.
        message.error(
          describeError(r.error, 'حُفظت الوثيقة لكن تعذّر إرفاق بعض الصفحات.'),
          6,
        );
        navigate(`/documents/${doc.id}`, { replace: true });
        return;
      }
    }
    setSaving(false);
    message.success(
      isCorrespondence(type) && doc.reference
        ? `حُفظت الوثيقة. رقم المراسلة: ${doc.reference}`
        : 'حُفظت الوثيقة',
      5,
    );
    navigate(`/documents/${doc.id}`, { replace: true });
  }

  return (
    <div className="page">
      <div className="back-bar">
        <button
          type="button"
          className="back-link back-button"
          onClick={() => (step === 'details' ? setStep('pages') : leave())}
        >
          <ArrowRightOutlined /> {step === 'details' ? 'الصفحات' : 'الوثائق'}
        </button>
      </div>
      <h1 className="member-title">تصوير وثيقة</h1>

      {step === 'pages' && (
        <>
          <div className="field-hint">
            صوّر كل صفحة وحدها، بالترتيب، على مكان مستوٍ ومضيء.
          </div>
          {pages.length > 0 && (
            <ol className="scan-pages">
              {pages.map((p, i) => (
                <li key={p.key} className={p.error ? 'is-failed' : undefined}>
                  {p.pdf ? (
                    <span className="scan-pdf">
                      <FilePdfOutlined />
                      PDF
                    </span>
                  ) : (
                    <img src={p.preview ?? ''} alt={`الصفحة ${i + 1}`} />
                  )}
                  <span className="scan-number ltr-num">{i + 1}</span>
                  <button
                    type="button"
                    className="scan-remove"
                    aria-label={`إزالة الصفحة ${i + 1}`}
                    onClick={() => remove(p)}
                  >
                    <CloseOutlined />
                  </button>
                  <span className="scan-state">
                    {p.error ? (
                      <Button
                        size="small"
                        danger
                        icon={<ReloadOutlined />}
                        onClick={() => send(p)}
                      >
                        أعد
                      </Button>
                    ) : p.photoId ? (
                      <CheckCircleFilled className="scan-ok" />
                    ) : (
                      <Progress
                        type="circle"
                        size={36}
                        percent={Math.round(p.progress * 100)}
                      />
                    )}
                  </span>
                </li>
              ))}
            </ol>
          )}
          {pages.find((p) => p.error) && (
            <Alert
              type="error"
              showIcon
              message={pages.find((p) => p.error)?.error}
            />
          )}
          <Button
            type={pages.length ? 'default' : 'primary'}
            block
            className="big-button"
            icon={<CameraOutlined />}
            onClick={() => camera.current?.click()}
          >
            {pages.length ? 'تصوير صفحة أخرى' : 'تصوير الصفحة الأولى'}
          </Button>
          <Button
            block
            size="large"
            icon={<PictureOutlined />}
            onClick={() => gallery.current?.click()}
          >
            من المعرض أو ملف PDF
          </Button>
          {pages.length > 0 && (
            <Button
              type="primary"
              block
              className="big-button"
              disabled={!ready}
              onClick={() => setStep('details')}
            >
              {sending ? 'إرسال الصفحات…' : `انتهيت (${pages.length})`}
            </Button>
          )}
          <input
            ref={camera}
            type="file"
            accept="image/*"
            capture="environment"
            hidden
            onChange={(e) => {
              add([...(e.target.files ?? [])]);
              e.target.value = '';
            }}
          />
          <input
            ref={gallery}
            type="file"
            accept="image/*,application/pdf"
            multiple
            hidden
            onChange={(e) => {
              add([...(e.target.files ?? [])]);
              e.target.value = '';
            }}
          />
        </>
      )}

      {step === 'details' && (
        <div className="card scan-details">
          <span className="field-label">ما هذه الوثيقة؟</span>
          <Radio.Group
            className="scan-types"
            value={type && QUICK.includes(type) ? type : null}
            onChange={(e) => setType(e.target.value)}
            optionType="button"
            buttonStyle="solid"
            options={QUICK.map((t) => ({
              value: t,
              label: QUICK_LABELS[t] ?? DOCUMENT_TYPE_LABELS[t],
            }))}
          />
          <Select<DocumentType>
            size="large"
            placeholder="نوع آخر…"
            value={type && !QUICK.includes(type) ? type : undefined}
            onChange={setType}
            options={DOCUMENT_TYPES.filter((t) => !QUICK.includes(t)).map(
              (t) => ({ value: t, label: DOCUMENT_TYPE_LABELS[t] }),
            )}
          />

          <label className="field-label" htmlFor="scan-title">
            العنوان
          </label>
          <Input
            id="scan-title"
            size="large"
            placeholder="مثلاً: رسالة الجماعة حول الماء"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />

          <label className="field-label" htmlFor="scan-date">
            تاريخ الوثيقة
          </label>
          <Input
            id="scan-date"
            size="large"
            type="date"
            className="ltr-num"
            value={date}
            onChange={(e) => setDate(e.target.value)}
          />

          <label className="field-label" htmlFor="scan-party">
            {type === 'CORRESPONDENCE_IN'
              ? 'من أرسلها؟'
              : type === 'CORRESPONDENCE_OUT'
                ? 'إلى من؟'
                : 'الجهة (اختياري)'}
          </label>
          <Select
            id="scan-party"
            size="large"
            allowClear
            showSearch
            optionFilterProp="label"
            placeholder="الجماعة، القيادة…"
            value={counterpartyId ?? undefined}
            onChange={(v) => setCounterpartyId(v ?? null)}
            options={present(parties?.counterparties?.nodes).map((c) => ({
              value: c.id,
              label: c.name,
            }))}
            notFoundContent="غير موجودة: تُضاف من الحاسوب، أو اكتبها في العنوان."
          />
          {isCorrespondence(type) && (
            <div className="field-hint">يُعطى رقم المراسلة تلقائياً.</div>
          )}

          {problem && <Alert type="error" showIcon message={problem} />}
          <Button
            type="primary"
            block
            className="big-button"
            loading={saving}
            onClick={save}
          >
            حفظ الوثيقة
          </Button>
        </div>
      )}
    </div>
  );
}
